/**
 * avatarFilenameParser.js
 * Robust ZIP ingestion parser for avatar asset filenames.
 * Parses filenames into normalized metadata for the avatar_assets table.
 *
 * Parser version: 1.0.0
 */

import {
  PARSER_VERSION,
  PARSER_STATUS,
  SUPPORTED_EXTENSIONS,
  MIME_TYPE_MAP,
  CATEGORY_PREFIX_MAP,
  FOLDER_TO_CATEGORY,
  KNOWN_COLORS,
  KNOWN_BODY_TYPES,
  KNOWN_GENDERS,
  KNOWN_EYE_STYLES,
  KNOWN_EYEBROW_STYLES,
  KNOWN_MOUTH_STYLES,
  KNOWN_HAIR_STYLES,
  KNOWN_WING_STYLES,
  KNOWN_DONKEY_HEAD_VARIANTS,
  KNOWN_COSTUME_VARIANTS,
  LAYER_ORDER_MAP,
} from '../config/avatarParserConfig';

// ─── TOKENIZER ────────────────────────────────────────────────────────────────

/**
 * Splits a filename (without extension) into lowercase tokens by underscore.
 * @param {string} nameWithoutExt
 * @returns {string[]}
 */
export const tokenize = (nameWithoutExt) => {
  return nameWithoutExt
    ?.toLowerCase()
    ?.trim()
    ?.split('_')
    ?.filter(Boolean) || [];
};

// ─── CATEGORY INFERENCE ───────────────────────────────────────────────────────

/**
 * Infer category from folder path.
 * @param {string} folderPath - e.g. "bodies" or "hair_styles"
 * @returns {string|null}
 */
export const inferCategoryFromFolder = (folderPath) => {
  if (!folderPath) return null;
  const parts = folderPath?.split('/')?.filter(Boolean);
  for (const part of parts) {
    if (FOLDER_TO_CATEGORY?.[part]) return FOLDER_TO_CATEGORY?.[part];
  }
  return null;
};

/**
 * Infer category from filename tokens using prefix rules.
 * Checks multi-token prefixes first (facial_hair, donkey_head, etc.) then single.
 * @param {string[]} tokens
 * @returns {string|null}
 */
export const inferCategoryFromTokens = (tokens) => {
  if (!tokens?.length) return null;

  // Check two-token prefixes first
  if (tokens?.length >= 2) {
    const twoToken = `${tokens?.[0]}_${tokens?.[1]}`;
    if (CATEGORY_PREFIX_MAP?.[twoToken]) return CATEGORY_PREFIX_MAP?.[twoToken];
  }

  // Check single-token prefix
  if (CATEGORY_PREFIX_MAP?.[tokens?.[0]]) return CATEGORY_PREFIX_MAP?.[tokens?.[0]];

  return null;
};

// ─── DISPLAY LABEL BUILDER ────────────────────────────────────────────────────

/**
 * Build a human-readable display label from parsed fields.
 * @param {string} category
 * @param {object} fields
 * @returns {string}
 */
const buildDisplayLabel = (category, fields) => {
  const cap = (s) => s ? s?.charAt(0)?.toUpperCase() + s?.slice(1)?.replace(/_/g, ' ') : '';

  switch (category) {
    case 'bodies':
      return `${cap(fields?.body_type_affinity)} Body`;

    case 'skin_tones':
      return `${cap(fields?.skin_tone)} Skin`;

    case 'eyes': {
      const parts = [cap(fields?.style_variant) + ' Eyes'];
      if (fields?.eye_color) parts?.push(cap(fields?.eye_color));
      return parts?.join(' / ');
    }

    case 'eyebrows':
      return `${cap(fields?.style_variant)} Eyebrows`;

    case 'mouths':
      return `${cap(fields?.style_variant)} Mouth`;

    case 'hair_styles': {
      const parts = [];
      if (fields?.gender_affinity) parts?.push(cap(fields?.gender_affinity));
      if (fields?.style_variant) parts?.push(cap(fields?.style_variant) + ' Hair');
      if (fields?.hair_color) parts?.push(cap(fields?.hair_color));
      return parts?.join(' / ') || 'Hair';
    }

    case 'facial_hair':
      return cap(fields?.style_variant) || 'Facial Hair';

    case 'clothing': {
      const parts = [cap(fields?.subcategory)];
      if (fields?.color_family) parts?.push(cap(fields?.color_family));
      return parts?.filter(Boolean)?.join(' / ') || 'Clothing';
    }

    case 'accessories': {
      if (fields?.subcategory && fields?.variant) {
        return `${cap(fields?.subcategory)} / ${cap(fields?.variant)}`;
      }
      return cap(fields?.style_variant) || cap(fields?.subcategory) || 'Accessory';
    }

    case 'fantasy': {
      const st = fields?.special_type;
      if (st === 'wings') {
        const parts = [`${cap(fields?.subcategory)} Wings`];
        if (fields?.style_variant) parts?.push(cap(fields?.style_variant));
        if (fields?.color_family) parts?.push(cap(fields?.color_family));
        return parts?.join(' / ');
      }
      if (st === 'donkey_head') {
        const parts = ['Donkey Head'];
        if (fields?.variant) parts?.push(cap(fields?.variant));
        return parts?.join(' / ');
      }
      if (st === 'donkey_costume' || st === 'lion_costume') {
        const animal = st === 'donkey_costume' ? 'Donkey' : 'Lion';
        const parts = [`${animal} Costume`];
        if (fields?.variant) parts?.push(cap(fields?.variant));
        return parts?.join(' / ');
      }
      if (st === 'jester_hat') {
        const parts = ['Jester Hat'];
        if (fields?.color_family) parts?.push(cap(fields?.color_family));
        return parts?.join(' / ');
      }
      return cap(st) || 'Fantasy';
    }

    default:
      return cap(category);
  }
};

// ─── LAYER ORDER RESOLVER ─────────────────────────────────────────────────────

const resolveLayerOrder = (category, specialType) => {
  if (category === 'fantasy') {
    if (specialType === 'wings') return LAYER_ORDER_MAP?.fantasy_wings;
    if (specialType === 'donkey_head') return LAYER_ORDER_MAP?.fantasy_donkey_head;
    if (specialType === 'donkey_costume' || specialType === 'lion_costume') return LAYER_ORDER_MAP?.fantasy_costume;
    if (specialType === 'jester_hat') return LAYER_ORDER_MAP?.fantasy_jester_hat;
    return LAYER_ORDER_MAP?.fantasy;
  }
  return LAYER_ORDER_MAP?.[category] ?? 50;
};

// ─── CATEGORY-SPECIFIC PARSERS ────────────────────────────────────────────────

const parseBodies = (tokens) => {
  // tokens: [body, male|female|neutral]
  const bodyType = tokens?.[1] || null;
  const warnings = [];
  if (!bodyType || !KNOWN_BODY_TYPES?.has(bodyType)) {
    warnings?.push(`Unknown body type: "${bodyType}". Expected: male, female, neutral.`);
  }
  return { fields: { body_type_affinity: bodyType }, warnings };
};

const parseSkinTones = (tokens) => {
  // tokens: [skin, tone_value]
  const skinTone = tokens?.[1] || null;
  const warnings = [];
  if (!skinTone) {
    warnings?.push('Missing skin tone value.');
  } else if (!KNOWN_COLORS?.has(skinTone)) {
    warnings?.push(`Unknown skin tone: "${skinTone}".`);
  }
  return { fields: { skin_tone: skinTone }, warnings };
};

const parseEyes = (tokens) => {
  // tokens: [eyes, style_variant, eye_color]
  const styleVariant = tokens?.[1] || null;
  const eyeColor = tokens?.[2] || null;
  const warnings = [];
  if (!styleVariant) warnings?.push('Missing eye style variant.');
  else if (!KNOWN_EYE_STYLES?.has(styleVariant)) warnings?.push(`Unknown eye style: "${styleVariant}".`);
  if (!eyeColor) warnings?.push('Missing eye color.');
  else if (!KNOWN_COLORS?.has(eyeColor)) warnings?.push(`Unknown eye color: "${eyeColor}".`);
  return { fields: { style_variant: styleVariant, eye_color: eyeColor }, warnings };
};

const parseEyebrows = (tokens) => {
  // tokens: [eyebrows, style_variant]
  const styleVariant = tokens?.[1] || null;
  const warnings = [];
  if (!styleVariant) warnings?.push('Missing eyebrow style variant.');
  else if (!KNOWN_EYEBROW_STYLES?.has(styleVariant)) warnings?.push(`Unknown eyebrow style: "${styleVariant}".`);
  return { fields: { style_variant: styleVariant }, warnings };
};

const parseMouths = (tokens) => {
  // tokens: [mouth, style_variant]
  const styleVariant = tokens?.[1] || null;
  const warnings = [];
  if (!styleVariant) warnings?.push('Missing mouth style variant.');
  else if (!KNOWN_MOUTH_STYLES?.has(styleVariant)) warnings?.push(`Unknown mouth style: "${styleVariant}".`);
  return { fields: { style_variant: styleVariant }, warnings };
};

const parseHairStyles = (tokens) => {
  // tokens: [hair, male|female, style_variant, hair_color]
  // Special case: hair_male_bald → no color
  const genderToken = tokens?.[1] || null;
  const warnings = [];

  if (!genderToken || !KNOWN_GENDERS?.has(genderToken)) {
    warnings?.push(`Unknown gender affinity: "${genderToken}". Expected: male, female.`);
  }

  const styleVariant = tokens?.[2] || null;
  if (!styleVariant) {
    warnings?.push('Missing hair style variant.');
  } else if (!KNOWN_HAIR_STYLES?.has(styleVariant)) {
    warnings?.push(`Unknown hair style: "${styleVariant}".`);
  }

  // Bald has no color
  let hairColor = null;
  if (styleVariant !== 'bald') {
    hairColor = tokens?.[3] || null;
    if (!hairColor) warnings?.push('Missing hair color.');
    else if (!KNOWN_COLORS?.has(hairColor)) warnings?.push(`Unknown hair color: "${hairColor}".`);
  }

  return {
    fields: {
      gender_affinity: genderToken,
      style_variant: styleVariant,
      hair_color: hairColor,
    },
    warnings,
  };
};

const parseFacialHair = (tokens) => {
  // tokens: [facial, hair, ...style_tokens]
  // Skip "facial" and "hair" prefix tokens, join remaining as style_variant
  // e.g. facial_hair_shakespeare_beard → style_variant = shakespeare_beard
  // e.g. facial_hair_mustache_thin → style_variant = mustache_thin
  const styleTokens = tokens?.slice(2); // skip "facial" and "hair"
  const styleVariant = styleTokens?.join('_') || null;
  const warnings = [];
  if (!styleVariant) warnings?.push('Missing facial hair style variant.');
  return { fields: { style_variant: styleVariant }, warnings };
};

const parseClothing = (tokens) => {
  // tokens: [clothing, ...subcategory_tokens, color_token]
  // Strategy: last token is color if it's a known color; everything between is subcategory
  const bodyTokens = tokens?.slice(1); // remove "clothing"
  const warnings = [];

  if (bodyTokens?.length === 0) {
    warnings?.push('Missing clothing subcategory and color.');
    return { fields: { subcategory: null, color_family: null }, warnings };
  }

  // Find last known color token
  let colorIndex = -1;
  for (let i = bodyTokens?.length - 1; i >= 0; i--) {
    if (KNOWN_COLORS?.has(bodyTokens?.[i])) {
      colorIndex = i;
      break;
    }
  }

  let subcategoryTokens, colorFamily;
  if (colorIndex > 0) {
    subcategoryTokens = bodyTokens?.slice(0, colorIndex);
    colorFamily = bodyTokens?.[colorIndex];
  } else if (colorIndex === 0) {
    // Only color, no subcategory
    subcategoryTokens = [];
    colorFamily = bodyTokens?.[0];
    warnings?.push('Could not determine clothing subcategory.');
  } else {
    // No known color found — treat all as subcategory
    subcategoryTokens = bodyTokens;
    colorFamily = null;
    warnings?.push('Could not determine clothing color.');
  }

  const subcategory = subcategoryTokens?.join('_') || null;
  if (!subcategory) warnings?.push('Missing clothing subcategory.');

  return { fields: { subcategory, color_family: colorFamily }, warnings };
};

const parseAccessories = (tokens) => {
  // tokens: [accessory, style_or_subcategory, optional_variant]
  // Simple accessories: accessory_quill, accessory_scroll, accessory_dagger, etc.
  // Compound: accessory_crown_gold, accessory_mask_comedy, accessory_flower_rose, etc.
  const warnings = [];
  const bodyTokens = tokens?.slice(1); // remove "accessory"

  if (bodyTokens?.length === 0) {
    warnings?.push('Missing accessory type.');
    return { fields: { style_variant: null, subcategory: null, variant: null }, warnings };
  }

  if (bodyTokens?.length === 1) {
    // Simple: quill, scroll, dagger, skull, lute, book
    return { fields: { style_variant: bodyTokens?.[0], subcategory: null, variant: null }, warnings };
  }

  // Two+ tokens: subcategory + variant (e.g. crown_gold, mask_comedy, flower_rose)
  const subcategory = bodyTokens?.[0];
  const variant = bodyTokens?.slice(1)?.join('_');
  return { fields: { style_variant: null, subcategory, variant }, warnings };
};

const parseFantasy = (tokens) => {
  // Determine special_type from first 1-2 tokens
  const warnings = [];

  // wings_fairy_*
  if (tokens?.[0] === 'wings') {
    const subcategory = tokens?.[1] || null; // fairy
    const styleVariant = tokens?.[2] || null; // small/large/butterfly/glow/leaf/crystal
    let colorFamily = tokens?.[3] || null;

    if (!subcategory) warnings?.push('Missing wings subcategory (e.g. fairy).');
    if (!styleVariant) warnings?.push('Missing wings style variant.');
    else if (!KNOWN_WING_STYLES?.has(styleVariant)) warnings?.push(`Unknown wing style: "${styleVariant}".`);
    if (!colorFamily) warnings?.push('Missing wings color.');
    else if (!KNOWN_COLORS?.has(colorFamily)) warnings?.push(`Unknown wing color: "${colorFamily}".`);

    return {
      fields: {
        special_type: 'wings',
        subcategory,
        style_variant: styleVariant,
        color_family: colorFamily,
      },
      warnings,
    };
  }

  // donkey_head_*
  if (tokens?.[0] === 'donkey' && tokens?.[1] === 'head') {
    const variant = tokens?.[2] || null;
    if (variant && !KNOWN_DONKEY_HEAD_VARIANTS?.has(variant)) {
      warnings?.push(`Unknown donkey head variant: "${variant}".`);
    }
    return {
      fields: { special_type: 'donkey_head', variant },
      warnings,
    };
  }

  // lion_costume_*
  if (tokens?.[0] === 'lion' && tokens?.[1] === 'costume') {
    const variant = tokens?.[2] || null;
    if (variant && !KNOWN_COSTUME_VARIANTS?.has(variant)) {
      warnings?.push(`Unknown lion costume variant: "${variant}".`);
    }
    return {
      fields: { special_type: 'lion_costume', subcategory: 'lion', variant },
      warnings,
    };
  }

  // donkey_costume_*
  if (tokens?.[0] === 'donkey' && tokens?.[1] === 'costume') {
    const variant = tokens?.[2] || null;
    if (variant && !KNOWN_COSTUME_VARIANTS?.has(variant)) {
      warnings?.push(`Unknown donkey costume variant: "${variant}".`);
    }
    return {
      fields: { special_type: 'donkey_costume', subcategory: 'donkey', variant },
      warnings,
    };
  }

  // jester_hat_*
  if (tokens?.[0] === 'jester' && tokens?.[1] === 'hat') {
    // Remaining tokens joined = color_family (e.g. red_gold, green_yellow, big_bells, no_bells)
    let colorFamily = tokens?.slice(2)?.join('_') || null;
    if (!colorFamily) warnings?.push('Missing jester hat color/variant.');
    return {
      fields: { special_type: 'jester_hat', color_family: colorFamily },
      warnings,
    };
  }

  // Unknown fantasy
  warnings?.push(`Unrecognized fantasy asset pattern: "${tokens?.join('_')}".`);
  return {
    fields: { special_type: tokens?.slice(0, 2)?.join('_') || tokens?.[0] || null },
    warnings,
  };
};

// ─── VALIDATION HELPER ────────────────────────────────────────────────────────

/**
 * Validate parsed result and return { status, warnings[] }.
 * @param {object} parsed - result from parseAvatarFilename
 * @param {string} folderCategory - category inferred from folder
 * @param {string} filenameCategory - category inferred from filename
 * @returns {{ status: string, warnings: string[] }}
 */
export const validateParsedAsset = (parsed, folderCategory, filenameCategory) => {
  const warnings = [...(parsed?.parser_warnings || [])];
  let status = PARSER_STATUS?.VALID;

  // Category mismatch between folder and filename
  if (folderCategory && filenameCategory && folderCategory !== filenameCategory) {
    warnings?.push(
      `Category mismatch: folder implies "${folderCategory}" but filename implies "${filenameCategory}". Using folder category.`
    );
    status = PARSER_STATUS?.REVIEW_REQUIRED;
  }

  // No category resolved
  if (!parsed?.category) {
    warnings?.push('Could not determine asset category.');
    return { status: PARSER_STATUS?.REVIEW_REQUIRED, warnings };
  }

  // Unsupported extension
  if (!parsed?.mime_type) {
    warnings?.push(`Unsupported file extension. Supported: jpg, jpeg, png, webp.`);
    return { status: PARSER_STATUS?.INVALID, warnings };
  }

  // Category-specific required field checks
  const f = parsed?.fields || {};
  switch (parsed?.category) {
    case 'bodies':
      if (!f?.body_type_affinity) { warnings?.push('Missing body_type_affinity.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'skin_tones':
      if (!f?.skin_tone) { warnings?.push('Missing skin_tone.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'eyes':
      if (!f?.style_variant) { warnings?.push('Missing eye style_variant.'); status = PARSER_STATUS?.WARNING; }
      if (!f?.eye_color) { warnings?.push('Missing eye_color.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'eyebrows':
      if (!f?.style_variant) { warnings?.push('Missing eyebrow style_variant.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'mouths':
      if (!f?.style_variant) { warnings?.push('Missing mouth style_variant.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'hair_styles':
      if (!f?.gender_affinity) { warnings?.push('Missing hair gender_affinity.'); status = PARSER_STATUS?.WARNING; }
      if (!f?.style_variant) { warnings?.push('Missing hair style_variant.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'facial_hair':
      if (!f?.style_variant) { warnings?.push('Missing facial_hair style_variant.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'clothing':
      if (!f?.subcategory) { warnings?.push('Missing clothing subcategory.'); status = PARSER_STATUS?.WARNING; }
      break;
    case 'fantasy':
      if (!f?.special_type) { warnings?.push('Missing fantasy special_type.'); status = PARSER_STATUS?.REVIEW_REQUIRED; }
      break;
    default:
      break;
  }

  // Escalate to review_required if there are parser warnings from category parsing
  if (status === PARSER_STATUS?.VALID && warnings?.length > 0) {
    status = PARSER_STATUS?.WARNING;
  }

  return { status, warnings };
};

// ─── MAIN PARSER ─────────────────────────────────────────────────────────────

/**
 * Parse a ZIP entry path into normalized avatar asset metadata.
 *
 * @param {string} zipEntryPath - e.g. "bodies/body_male.jpg" or "body_male.jpg"
 * @returns {object} - Normalized metadata record
 */
export const parseAvatarFilename = (zipEntryPath) => {
  if (!zipEntryPath) {
    return {
      parser_status: PARSER_STATUS?.INVALID,
      parser_warnings: ['Empty file path provided.'],
      parser_version: PARSER_VERSION,
    };
  }

  // ── Split path into folder and filename ──────────────────────────────────
  const pathParts = zipEntryPath?.split('/')?.filter(Boolean);
  const rawFilename = pathParts?.[pathParts?.length - 1];
  const folderPath = pathParts?.slice(0, -1)?.join('/');

  // ── Extract extension ────────────────────────────────────────────────────
  const dotIndex = rawFilename?.lastIndexOf('.');
  const ext = dotIndex >= 0 ? rawFilename?.slice(dotIndex + 1)?.toLowerCase() : '';
  const nameWithoutExt = dotIndex >= 0 ? rawFilename?.slice(0, dotIndex)?.toLowerCase() : rawFilename?.toLowerCase();

  // ── Validate extension ───────────────────────────────────────────────────
  const mimeType = MIME_TYPE_MAP?.[ext] || null;
  if (!SUPPORTED_EXTENSIONS?.has(ext)) {
    return {
      original_filename: rawFilename,
      original_zip_path: zipEntryPath,
      slug: nameWithoutExt?.replace(/_/g, '-'),
      mime_type: null,
      parser_status: PARSER_STATUS?.INVALID,
      parser_warnings: [`Unsupported extension ".${ext}". Supported: jpg, jpeg, png, webp.`],
      parser_version: PARSER_VERSION,
    };
  }

  // ── Tokenize ─────────────────────────────────────────────────────────────
  const tokens = tokenize(nameWithoutExt);

  if (tokens?.length === 0) {
    return {
      original_filename: rawFilename,
      original_zip_path: zipEntryPath,
      slug: nameWithoutExt,
      mime_type: mimeType,
      parser_status: PARSER_STATUS?.INVALID,
      parser_warnings: ['Empty filename after tokenization.'],
      parser_version: PARSER_VERSION,
    };
  }

  // ── Category inference ───────────────────────────────────────────────────
  const folderCategory = inferCategoryFromFolder(folderPath);
  const filenameCategory = inferCategoryFromTokens(tokens);

  // Folder takes priority; flag mismatch
  const category = folderCategory || filenameCategory || null;

  // ── Category-specific parsing ────────────────────────────────────────────
  let categoryFields = {};
  let categoryWarnings = [];

  if (category) {
    let result;
    switch (category) {
      case 'bodies':
        result = parseBodies(tokens);
        break;
      case 'eyes':
        result = parseEyes(tokens);
        break;
      case 'eyebrows':
        result = parseEyebrows(tokens);
        break;
      case 'mouths':
        result = parseMouths(tokens);
        break;
      case 'hair_styles':
        result = parseHairStyles(tokens);
        break;
      case 'facial_hair':
        result = parseFacialHair(tokens);
        break;
      case 'clothing':
        result = parseClothing(tokens);
        break;
      case 'accessories':
        result = parseAccessories(tokens);
        break;
      case 'fantasy':
        result = parseFantasy(tokens);
        break;
      default:
        result = { fields: {}, warnings: [`No parser rule for category "${category}".`] };
    }
    categoryFields = result?.fields || {};
    categoryWarnings = result?.warnings || [];
  }

  // ── Slug ─────────────────────────────────────────────────────────────────
  const slug = nameWithoutExt?.replace(/_/g, '-');

  // ── Storage path ─────────────────────────────────────────────────────────
  const storagePath = category ? `v1/${category}/${rawFilename}` : null;

  // ── Layer order ───────────────────────────────────────────────────────────
  const layerOrder = resolveLayerOrder(category, categoryFields?.special_type);

  // ── Build normalized fields object ───────────────────────────────────────
  const fields = {
    category,
    subcategory: categoryFields?.subcategory || null,
    special_type: categoryFields?.special_type || null,
    body_type_affinity: categoryFields?.body_type_affinity || null,
    gender_affinity: categoryFields?.gender_affinity || null,
    color_family: categoryFields?.color_family || null,
    eye_color: categoryFields?.eye_color || null,
    hair_color: categoryFields?.hair_color || null,
    skin_tone: categoryFields?.skin_tone || null,
    style_variant: categoryFields?.style_variant || null,
    variant: categoryFields?.variant || null,
  };

  // ── Display label ─────────────────────────────────────────────────────────
  const displayLabel = category ? buildDisplayLabel(category, { ...fields, ...categoryFields }) : rawFilename;

  // ── Tags ──────────────────────────────────────────────────────────────────
  const tags = buildTags(category, fields);

  // ── Validation ────────────────────────────────────────────────────────────
  const allWarnings = [...categoryWarnings];
  const parsedForValidation = { category, mime_type: mimeType, fields, parser_warnings: allWarnings };
  const validation = validateParsedAsset(parsedForValidation, folderCategory, filenameCategory);

  const finalWarnings = validation?.warnings;
  const finalStatus = !category ? PARSER_STATUS?.REVIEW_REQUIRED : validation?.status;

  // ── Final result ──────────────────────────────────────────────────────────
  return {
    // File info
    original_filename: rawFilename,
    original_zip_path: zipEntryPath,
    mime_type: mimeType,
    file_extension: ext,

    // Resolved category
    category,
    folder_category: folderCategory,
    filename_category: filenameCategory,

    // Normalized metadata fields
    ...fields,
    display_label: displayLabel,
    slug,
    storage_path: storagePath,
    layer_order: layerOrder,
    tags,

    // Parser metadata
    parser_status: finalStatus,
    parser_warnings: finalWarnings,
    parser_version: PARSER_VERSION,
  };
};

// ─── TAG BUILDER ─────────────────────────────────────────────────────────────

const buildTags = (category, fields) => {
  const tags = [];
  if (category) tags?.push(category);
  if (fields?.gender_affinity) tags?.push(fields?.gender_affinity);
  if (fields?.body_type_affinity) tags?.push(fields?.body_type_affinity);
  if (fields?.style_variant) tags?.push(fields?.style_variant);
  if (fields?.color_family) tags?.push(fields?.color_family);
  if (fields?.eye_color) tags?.push(fields?.eye_color);
  if (fields?.hair_color) tags?.push(fields?.hair_color);
  if (fields?.skin_tone) tags?.push(fields?.skin_tone);
  if (fields?.subcategory) tags?.push(fields?.subcategory);
  if (fields?.special_type) tags?.push(fields?.special_type);
  if (fields?.variant) tags?.push(fields?.variant);
  return [...new Set(tags)]?.filter(Boolean);
};

// ─── BATCH PARSER ────────────────────────────────────────────────────────────

/**
 * Parse multiple ZIP entry paths and return results with summary.
 * @param {string[]} zipEntryPaths
 * @returns {{ results: object[], summary: object }}
 */
export const parseZipEntries = (zipEntryPaths) => {
  const results = zipEntryPaths?.map(path => parseAvatarFilename(path));

  const summary = {
    total: results?.length,
    valid: results?.filter(r => r?.parser_status === PARSER_STATUS?.VALID)?.length,
    warning: results?.filter(r => r?.parser_status === PARSER_STATUS?.WARNING)?.length,
    review_required: results?.filter(r => r?.parser_status === PARSER_STATUS?.REVIEW_REQUIRED)?.length,
    invalid: results?.filter(r => r?.parser_status === PARSER_STATUS?.INVALID)?.length,
  };

  return { results, summary };
};

// ─── DB PAYLOAD MAPPER ────────────────────────────────────────────────────────

/**
 * Map a parsed result to an avatar_assets table insert payload.
 * @param {object} parsed - result from parseAvatarFilename
 * @param {string} categoryId - UUID of the resolved category
 * @param {string} userId - UUID of the uploading user
 * @returns {object}
 */
export const mapParsedToDbPayload = (parsed, categoryId, userId) => ({
  slug: parsed?.slug,
  filename: parsed?.original_filename,
  original_filename: parsed?.original_zip_path,
  category_id: categoryId || null,
  storage_bucket: 'avatar-assets',
  storage_path: parsed?.storage_path,
  mime_type: parsed?.mime_type,
  layer_order: parsed?.layer_order,
  body_type_affinity: parsed?.body_type_affinity,
  gender_affinity: parsed?.gender_affinity,
  color_family: parsed?.color_family,
  eye_color: parsed?.eye_color,
  hair_color: parsed?.hair_color,
  skin_tone: parsed?.skin_tone,
  style_variant: parsed?.style_variant,
  special_type: parsed?.special_type,
  subcategory: parsed?.subcategory,
  variant: parsed?.variant,
  display_label: parsed?.display_label,
  tags: parsed?.tags,
  parser_status: parsed?.parser_status,
  parser_warnings: parsed?.parser_warnings?.length ? parsed?.parser_warnings : null,
  parser_version: parsed?.parser_version,
  is_active: true,
  is_student_visible: true,
  // created_by is intentionally omitted from the upsert payload so that
  // on conflict (slug already exists) the DB trigger preserves the original
  // creator. For new inserts, the DB default / trigger will use userId below
  // via the ingestZip caller which passes it separately when needed.
  // updated_by and updated_at are always refreshed on every upsert.
  created_by: userId,
  updated_by: userId,
  updated_at: new Date()?.toISOString(),
});
